// apps/share-web/src/versions/version-choice.ts
//
// Личный выбор версии песни в едином сетлисте (единый сетлист, фаза 3) — какую
// версию (сессию/запись) этот человек слушал последней у песни с несколькими
// версиями. Хранится на устройстве (localStorage), не на сервере: это личное
// предпочтение, а не состояние сетлиста.
//
// Ключ — 'bw-version' + личность + сетлист + песня, разделитель тот же U+001F
// (unit separator), что и в offline-keys.ts: не встречается в uuid и его
// невозможно ввести в имя команды/сетлиста/песни, так что части ключа не
// склеятся друг с другом.
//
// Вся работа с storage — в try/catch: приватный режим браузера бросает на
// getItem/setItem, и это должно вырождаться в «выбора нет» / «ничего не
// сохранилось», а не в краш экрана.

const SEP = '\u001f';

export interface VersionRef {
  shareId: string;
  sessionId: string | null;
}

/** Ключ личного выбора версии. `userId === null` — анонимный гость. */
export function choiceKey(userId: string | null, setlistId: string, songId: string): string {
  return `bw-version${SEP}${userId ?? 'guest'}${SEP}${setlistId}${SEP}${songId}`;
}

/** Читает сохранённый выбор. Ошибка storage (приватный режим) → `null` — как будто выбора нет. */
export function readChoice(kv: Pick<Storage, 'getItem'>, key: string): string | null {
  try {
    return kv.getItem(key);
  } catch {
    return null;
  }
}

/** Запоминает выбор: sessionId версии, а если его нет (импорт без сессии) — shareId. */
export function writeChoice(kv: Pick<Storage, 'setItem'>, key: string, ref: VersionRef): void {
  try {
    kv.setItem(key, ref.sessionId ?? ref.shareId);
  } catch {
    // приватный режим / квота исчерпана — выбор просто не запомнится на этот раз
  }
}

/**
 * Находит версию по сохранённому выбору: сперва по `sessionId`, затем по
 * `shareId` (версия без сессии). Ничего не сохранено, ничего не найдено
 * (версия удалена) или список версий пуст → `versions[0]` (новейшая, versions
 * идут newest-first) с `isNewest: true`; пустой список → `version: null`.
 */
export function resolveVersion<T extends VersionRef>(
  versions: T[],
  stored: string | null,
): { version: T | null; isNewest: boolean } {
  if (versions.length === 0) return { version: null, isNewest: true };
  if (!stored) return { version: versions[0]!, isNewest: true };
  let idx = versions.findIndex((v) => v.sessionId === stored);
  if (idx === -1) idx = versions.findIndex((v) => v.shareId === stored);
  if (idx === -1) return { version: versions[0]!, isNewest: true };
  return { version: versions[idx]!, isNewest: idx === 0 };
}
